import React from 'react';
import { User, Scale, Ruler, Activity } from 'lucide-react';

/**
 * Компонент секции формы для ввода персональных данных
 */
const PersonalDataSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  activityLevels
}) => {
  const units = formData.measurementSystem === 'metric' 
    ? { height: 'см', weight: 'кг' } 
    : { height: 'дюйм', weight: 'фунт' };

  return (
    <div className="mb-6 bg-gray-50 dark:bg-gray-800/30 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <User className="w-5 h-5 mr-2 text-emerald-500" />
        Персональные данные
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Возраст */}
        <div>
          <label htmlFor="age" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Возраст (лет)
          </label>
          <input
            type="text"
            id="age"
            name="age"
            value={formData.age}
            onChange={handleInputChange}
            className={`form-input ${errors.age ? 'input-error' : ''}`}
            placeholder="Введите возраст"
          />
          {errors.age && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.age}
            </p>
          )}
        </div>

        {/* Рост */}
        <div>
          <label htmlFor="height" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Ruler className="w-4 h-4 mr-1 text-emerald-500" />
            Рост ({units.height})
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="text"
              id="height"
              name="height"
              value={formData.height}
              onChange={handleInputChange}
              className={`form-input pr-12 ${errors.height ? 'input-error' : ''}`}
              placeholder={`Введите рост в ${units.height}`}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                {units.height}
              </span>
            </div>
          </div>
          {errors.height && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.height}
            </p>
          )}
        </div>

        {/* Вес */}
        <div>
          <label htmlFor="weight" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Scale className="w-4 h-4 mr-1 text-emerald-500" />
            Вес ({units.weight})
          </label>
          <div className="mt-1 relative rounded-md shadow-sm">
            <input
              type="text"
              id="weight"
              name="weight"
              value={formData.weight}
              onChange={handleInputChange}
              className={`form-input pr-12 ${errors.weight ? 'input-error' : ''}`}
              placeholder={`Введите вес в ${units.weight}`}
            />
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <span className="text-gray-500 dark:text-gray-400 sm:text-sm">
                {units.weight}
              </span>
            </div>
          </div>
          {errors.weight && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.weight}
            </p>
          )}
        </div>
      </div>

      {/* Уровень физической активности */}
      <div className="mt-4">
        <label htmlFor="activityLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
          <Activity className="w-4 h-4 mr-1 text-emerald-500" />
          Уровень физической активности
        </label>
        <select
          id="activityLevel"
          name="activityLevel"
          value={formData.activityLevel}
          onChange={handleInputChange}
          className="form-select"
        >
          {activityLevels.map(level => (
            <option key={level.id} value={level.id}>
              {level.name}
            </option>
          ))}
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {activityLevels.find(level => level.id === formData.activityLevel)?.description}
        </p>
      </div>

      {/* Информация о персональных данных */}
      <div className="mt-4 bg-emerald-50 dark:bg-emerald-900/20 p-3 rounded-lg border border-emerald-100 dark:border-emerald-800 text-xs text-emerald-700 dark:text-emerald-300">
        <p>
          Ваши персональные данные помогут нам предоставить более точные рекомендации 
          и оценить риски для здоровья. Данные не хранятся на сервере и используются только для расчетов.
        </p>
      </div>
    </div>
  );
};

export default PersonalDataSection;